// components/ui/SubjectCard.tsx
import { Link } from 'react-router-dom';
import type { Subject } from '../../types/course';

// Default icons/colors for subjects based on common MBA subjects
const SUBJECT_DEFAULTS: Record<string, { emoji: string; color: string }> = {
  marketing: { emoji: '📊', color: '#0D9488' },
  hr: { emoji: '👥', color: '#DB2777' },
  finance: { emoji: '💰', color: '#D97706' },
  accounting: { emoji: '📒', color: '#2563EB' },
  economics: { emoji: '📈', color: '#7C3AED' },
  management: { emoji: '🎯', color: '#059669' },
  operations: { emoji: '⚙️', color: '#EA580C' },
  strategy: { emoji: '♟️', color: '#4F46E5' },
  it: { emoji: '💻', color: '#0891B2' },
  law: { emoji: '⚖️', color: '#9333EA' },
};

function getSubjectStyle(name: string, icon?: string, color?: string) {
  const key = name.toLowerCase().replace(/\s+/g, '');
  const defaults = Object.entries(SUBJECT_DEFAULTS).find(([k]) => key.includes(k));
  return {
    emoji: icon || defaults?.[1].emoji || '📘',
    color: color || defaults?.[1].color || '#6366F1',
  };
}

interface SubjectCardProps {
  subject: Subject;
  topicId: string;
  index: number;
}

export default function SubjectCard({ subject, topicId, index }: SubjectCardProps) {
  const style = getSubjectStyle(subject.name, subject.icon, subject.color);
  const chapterCount = subject.chapters?.length || 0;
  const delay = Math.min(index * 60, 300);

  return (
    <Link
      to={`/course/${topicId}/${subject.id}`}
      className="touch-card flex items-center gap-4 bg-white rounded-2xl p-4 shadow-soft hover:shadow-card"
      style={{ animationDelay: `${delay}ms`, animation: `slideRight 0.4s ease-out ${delay}ms both` }}
    >
      {/* Icon Square */}
      <div
        className="subject-icon shadow-sm"
        style={{ backgroundColor: style.color + '18', color: style.color }}
      >
        <span className="text-2xl">{style.emoji}</span>
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <h3 className="text-base font-semibold text-surface-900 truncate">
          {subject.name}
        </h3>
        {subject.description && (
          <p className="text-xs text-surface-300 mt-0.5 truncate">
            {subject.description}
          </p>
        )}
      </div>

      {/* Badge */}
      <span
        className="text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap"
        style={{ backgroundColor: style.color + '15', color: style.color }}
      >
        {chapterCount} {chapterCount === 1 ? 'ch' : 'chs'}
      </span>
    </Link>
  );
}
